import React, { useState, useEffect, useCallback } from 'react';
import { 
  Palette, Save, RotateCcw, Eye, Monitor, Smartphone,
  Loader2, Check, Sparkles, Layout, Globe, Heart,
  ChevronRight, Star, Settings, Edit3, Type, Square, 
  Columns, Image, Navigation, ChevronDown, ChevronUp, Home
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { useToast } from '../hooks/use-toast';
import { useTheme } from '../context/ThemeContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Design Presets
const designPresets = {
  // Qbool Original Theme (Based on screenshot)
  qboolOriginal: {
    id: 'qboolOriginal', name: 'Qbool Original', category: 'gradient', preview: '🕌',
    description: 'Dark navy to teal - Official qbool design',
    theme: {
      primaryColor: '#2dd4bf', secondaryColor: '#1e3a5f', accentColor: '#5eead4',
      backgroundColor: '#f0fdfa', cardBackground: '#ffffff',
      headerBackground: 'linear-gradient(135deg, #1e3a5f 0%, #2dd4bf 100%)',
      footerBackground: 'linear-gradient(135deg, #1e3a5f 0%, #2dd4bf 100%)',
      textColor: '#1f2937', textLight: '#6b7280', headerText: '#ffffff', footerText: '#ffffff',
      linkColor: '#2dd4bf', buttonPrimary: 'linear-gradient(135deg, #1e3a5f 0%, #2dd4bf 100%)',
      buttonPrimaryText: '#ffffff', buttonSecondary: '#ccfbf1', buttonSecondaryText: '#134e4a',
      borderRadius: 'medium', fontFamily: 'Poppins', headingFont: 'Poppins',
      // Homepage Section Colors
      heroOverlay: 'linear-gradient(135deg, rgba(30,58,95,0.9) 0%, rgba(45,212,191,0.7) 100%)',
      registrationBg: 'linear-gradient(135deg, #f0fdfa 0%, #ccfbf1 100%)',
      registrationText: '#1f2937',
      whyChooseUsBg: 'linear-gradient(135deg, #1e3a5f 0%, #2dd4bf 100%)',
      whyChooseUsText: '#ffffff',
      successStoriesBg: '#f0fdfa',
      successStoriesText: '#1f2937',
      ctaBg: 'linear-gradient(135deg, #1e3a5f 0%, #2dd4bf 100%)',
      ctaText: '#ffffff',
    }
  },
  // Gradient Themes
  sunsetGlow: {
    id: 'sunsetGlow', name: 'Sunset Glow', category: 'gradient', preview: '🌅',
    description: 'Warm orange to pink gradient',
    theme: {
      primaryColor: '#f97316', secondaryColor: '#ec4899', accentColor: '#fbbf24',
      backgroundColor: '#fffbeb', cardBackground: '#ffffff',
      headerBackground: 'linear-gradient(135deg, #f97316 0%, #ec4899 100%)',
      footerBackground: 'linear-gradient(135deg, #f97316 0%, #ec4899 100%)',
      textColor: '#1f2937', textLight: '#6b7280', headerText: '#ffffff', footerText: '#ffffff',
      linkColor: '#f97316', buttonPrimary: 'linear-gradient(135deg, #f97316 0%, #ec4899 100%)',
      buttonPrimaryText: '#ffffff', buttonSecondary: '#fef3c7', buttonSecondaryText: '#92400e',
      borderRadius: 'large', fontFamily: 'Poppins', headingFont: 'Poppins',
      // Homepage Section Colors
      heroOverlay: 'linear-gradient(135deg, rgba(249,115,22,0.8) 0%, rgba(236,72,153,0.6) 100%)',
      registrationBg: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
      registrationText: '#1f2937',
      whyChooseUsBg: 'linear-gradient(135deg, #f97316 0%, #ec4899 100%)',
      whyChooseUsText: '#ffffff',
      successStoriesBg: '#fffbeb',
      successStoriesText: '#1f2937',
      ctaBg: 'linear-gradient(135deg, #f97316 0%, #ec4899 100%)',
      ctaText: '#ffffff',
    }
  },
  oceanBreeze: {
    id: 'oceanBreeze', name: 'Ocean Breeze', category: 'gradient', preview: '🌊',
    description: 'Blue to teal gradient',
    theme: {
      primaryColor: '#0ea5e9', secondaryColor: '#14b8a6', accentColor: '#38bdf8',
      backgroundColor: '#f0fdfa', cardBackground: '#ffffff',
      headerBackground: 'linear-gradient(135deg, #0ea5e9 0%, #14b8a6 100%)',
      footerBackground: 'linear-gradient(135deg, #0ea5e9 0%, #14b8a6 100%)',
      textColor: '#0f172a', textLight: '#64748b', headerText: '#ffffff', footerText: '#ffffff',
      linkColor: '#0ea5e9', buttonPrimary: 'linear-gradient(135deg, #0ea5e9 0%, #14b8a6 100%)',
      buttonPrimaryText: '#ffffff', buttonSecondary: '#ccfbf1', buttonSecondaryText: '#115e59',
      borderRadius: 'medium', fontFamily: 'Inter', headingFont: 'Poppins',
      // Homepage Section Colors
      heroOverlay: 'linear-gradient(135deg, rgba(14,165,233,0.8) 0%, rgba(20,184,166,0.6) 100%)',
      registrationBg: 'linear-gradient(135deg, #f0fdfa 0%, #ccfbf1 100%)',
      registrationText: '#0f172a',
      whyChooseUsBg: 'linear-gradient(135deg, #0ea5e9 0%, #14b8a6 100%)',
      whyChooseUsText: '#ffffff',
      successStoriesBg: '#f0fdfa',
      successStoriesText: '#0f172a',
      ctaBg: 'linear-gradient(135deg, #0ea5e9 0%, #14b8a6 100%)',
      ctaText: '#ffffff',
    }
  },
  purpleDream: {
    id: 'purpleDream', name: 'Purple Dream', category: 'gradient', preview: '💜',
    description: 'Purple to pink gradient',
    theme: {
      primaryColor: '#8b5cf6', secondaryColor: '#ec4899', accentColor: '#c084fc',
      backgroundColor: '#faf5ff', cardBackground: '#ffffff',
      headerBackground: 'linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%)',
      footerBackground: 'linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%)',
      textColor: '#1e1b4b', textLight: '#6b7280', headerText: '#ffffff', footerText: '#ffffff',
      linkColor: '#8b5cf6', buttonPrimary: 'linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%)',
      buttonPrimaryText: '#ffffff', buttonSecondary: '#f3e8ff', buttonSecondaryText: '#6b21a8',
      borderRadius: 'full', fontFamily: 'Nunito', headingFont: 'Poppins',
      // Homepage Section Colors
      heroOverlay: 'linear-gradient(135deg, rgba(139,92,246,0.8) 0%, rgba(236,72,153,0.6) 100%)',
      registrationBg: 'linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%)',
      registrationText: '#1e1b4b',
      whyChooseUsBg: 'linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%)',
      whyChooseUsText: '#ffffff',
      successStoriesBg: '#faf5ff',
      successStoriesText: '#1e1b4b',
      ctaBg: 'linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%)',
      ctaText: '#ffffff',
    }
  },
  emeraldForest: {
    id: 'emeraldForest', name: 'Emerald Forest', category: 'gradient', preview: '🌲',
    description: 'Green to teal gradient',
    theme: {
      primaryColor: '#10b981', secondaryColor: '#0d9488', accentColor: '#34d399',
      backgroundColor: '#ecfdf5', cardBackground: '#ffffff',
      headerBackground: 'linear-gradient(135deg, #10b981 0%, #0d9488 100%)',
      footerBackground: 'linear-gradient(135deg, #10b981 0%, #0d9488 100%)',
      textColor: '#064e3b', textLight: '#6b7280', headerText: '#ffffff', footerText: '#ffffff',
      linkColor: '#10b981', buttonPrimary: 'linear-gradient(135deg, #10b981 0%, #0d9488 100%)',
      buttonPrimaryText: '#ffffff', buttonSecondary: '#d1fae5', buttonSecondaryText: '#065f46',
      borderRadius: 'medium', fontFamily: 'Inter', headingFont: 'Montserrat',
      // Homepage Section Colors
      heroOverlay: 'linear-gradient(135deg, rgba(16,185,129,0.8) 0%, rgba(13,148,136,0.6) 100%)',
      registrationBg: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
      registrationText: '#064e3b',
      whyChooseUsBg: 'linear-gradient(135deg, #10b981 0%, #0d9488 100%)',
      whyChooseUsText: '#ffffff',
      successStoriesBg: '#ecfdf5',
      successStoriesText: '#064e3b',
      ctaBg: 'linear-gradient(135deg, #10b981 0%, #0d9488 100%)',
      ctaText: '#ffffff',
    }
  },
  midnightAurora: {
    id: 'midnightAurora', name: 'Midnight Aurora', category: 'gradient', preview: '🌌',
    description: 'Dark purple to blue gradient',
    theme: {
      primaryColor: '#7c3aed', secondaryColor: '#3b82f6', accentColor: '#a78bfa',
      backgroundColor: '#0f172a', cardBackground: '#1e293b',
      headerBackground: 'linear-gradient(135deg, #7c3aed 0%, #3b82f6 100%)',
      footerBackground: 'linear-gradient(135deg, #7c3aed 0%, #3b82f6 100%)',
      textColor: '#f1f5f9', textLight: '#94a3b8', headerText: '#ffffff', footerText: '#ffffff',
      linkColor: '#a78bfa', buttonPrimary: 'linear-gradient(135deg, #7c3aed 0%, #3b82f6 100%)',
      buttonPrimaryText: '#ffffff', buttonSecondary: '#334155', buttonSecondaryText: '#e2e8f0',
      borderRadius: 'medium', fontFamily: 'Inter', headingFont: 'Poppins',
      // Homepage Section Colors
      heroOverlay: 'linear-gradient(135deg, rgba(124,58,237,0.9) 0%, rgba(59,130,246,0.7) 100%)',
      registrationBg: '#1e293b',
      registrationText: '#f1f5f9',
      whyChooseUsBg: 'linear-gradient(135deg, #7c3aed 0%, #3b82f6 100%)',
      whyChooseUsText: '#ffffff',
      successStoriesBg: '#0f172a',
      successStoriesText: '#f1f5f9',
      ctaBg: 'linear-gradient(135deg, #7c3aed 0%, #3b82f6 100%)',
      ctaText: '#ffffff',
    }
  },
  rosePetal: {
    id: 'rosePetal', name: 'Rose Petal', category: 'gradient', preview: '🌹',
    description: 'Soft pink gradient',
    theme: {
      primaryColor: '#f43f5e', secondaryColor: '#ec4899', accentColor: '#fb7185',
      backgroundColor: '#fff1f2', cardBackground: '#ffffff',
      headerBackground: 'linear-gradient(135deg, #f43f5e 0%, #ec4899 100%)',
      footerBackground: 'linear-gradient(135deg, #f43f5e 0%, #ec4899 100%)',
      textColor: '#1f2937', textLight: '#6b7280', headerText: '#ffffff', footerText: '#ffffff',
      linkColor: '#f43f5e', buttonPrimary: 'linear-gradient(135deg, #f43f5e 0%, #ec4899 100%)',
      buttonPrimaryText: '#ffffff', buttonSecondary: '#fce7f3', buttonSecondaryText: '#9d174d',
      borderRadius: 'full', fontFamily: 'Nunito', headingFont: 'Playfair Display',
      // Homepage Section Colors
      heroOverlay: 'linear-gradient(135deg, rgba(244,63,94,0.8) 0%, rgba(236,72,153,0.6) 100%)',
      registrationBg: 'linear-gradient(135deg, #fff1f2 0%, #fce7f3 100%)',
      registrationText: '#1f2937',
      whyChooseUsBg: 'linear-gradient(135deg, #f43f5e 0%, #ec4899 100%)',
      whyChooseUsText: '#ffffff',
      successStoriesBg: '#fff1f2',
      successStoriesText: '#1f2937',
      ctaBg: 'linear-gradient(135deg, #f43f5e 0%, #ec4899 100%)',
      ctaText: '#ffffff',
    }
  },
  // Solid Themes
  modernMinimal: {
    id: 'modernMinimal', name: 'Modern Minimal', category: 'style', preview: '⚪',
    description: 'Clean and minimal',
    theme: {
      primaryColor: '#E91E63', secondaryColor: '#9333ea', accentColor: '#10b981',
      backgroundColor: '#ffffff', cardBackground: '#ffffff',
      headerBackground: '#ffffff', footerBackground: '#1f2937',
      textColor: '#1f2937', textLight: '#6b7280', headerText: '#1f2937', footerText: '#ffffff',
      linkColor: '#E91E63', buttonPrimary: '#E91E63',
      buttonPrimaryText: '#ffffff', buttonSecondary: '#f3f4f6', buttonSecondaryText: '#374151',
      borderRadius: 'medium', fontFamily: 'Inter', headingFont: 'Poppins',
      // Homepage Section Colors
      heroOverlay: 'linear-gradient(to right, rgba(0,0,0,0.7), rgba(0,0,0,0.3))',
      registrationBg: 'linear-gradient(135deg, #FDF8F4 0%, #f0fdf4 100%)',
      registrationText: '#1f2937',
      whyChooseUsBg: 'linear-gradient(135deg, #1e3a5f 0%, #E91E63 100%)',
      whyChooseUsText: '#ffffff',
      successStoriesBg: '#FDF8F4',
      successStoriesText: '#1f2937',
      ctaBg: 'linear-gradient(135deg, #E91E63 0%, #9333ea 100%)',
      ctaText: '#ffffff',
    }
  },
  darkMode: {
    id: 'darkMode', name: 'Dark Mode', category: 'style', preview: '🌙',
    description: 'Dark elegant theme',
    theme: {
      primaryColor: '#10b981', secondaryColor: '#6366f1', accentColor: '#f59e0b',
      backgroundColor: '#111827', cardBackground: '#1f2937',
      headerBackground: '#0f172a', footerBackground: '#0f172a',
      textColor: '#f9fafb', textLight: '#9ca3af', headerText: '#ffffff', footerText: '#9ca3af',
      linkColor: '#10b981', buttonPrimary: '#10b981',
      buttonPrimaryText: '#ffffff', buttonSecondary: '#374151', buttonSecondaryText: '#f9fafb',
      borderRadius: 'medium', fontFamily: 'Inter', headingFont: 'Inter',
      // Homepage Section Colors
      heroOverlay: 'linear-gradient(to right, rgba(0,0,0,0.9), rgba(0,0,0,0.5))',
      registrationBg: '#1f2937',
      registrationText: '#f9fafb',
      whyChooseUsBg: 'linear-gradient(135deg, #10b981 0%, #6366f1 100%)',
      whyChooseUsText: '#ffffff',
      successStoriesBg: '#111827',
      successStoriesText: '#f9fafb',
      ctaBg: 'linear-gradient(135deg, #10b981 0%, #6366f1 100%)',
      ctaText: '#ffffff',
    }
  },
  warmEarth: {
    id: 'warmEarth', name: 'Warm Earth', category: 'style', preview: '🍂',
    description: 'Earthy warm tones',
    theme: {
      primaryColor: '#d97706', secondaryColor: '#b45309', accentColor: '#fbbf24',
      backgroundColor: '#fffbeb', cardBackground: '#ffffff',
      headerBackground: '#78350f', footerBackground: '#78350f',
      textColor: '#1c1917', textLight: '#78716c', headerText: '#ffffff', footerText: '#fef3c7',
      linkColor: '#d97706', buttonPrimary: '#d97706',
      buttonPrimaryText: '#ffffff', buttonSecondary: '#fef3c7', buttonSecondaryText: '#92400e',
      borderRadius: 'small', fontFamily: 'Georgia', headingFont: 'Georgia',
      // Homepage Section Colors
      heroOverlay: 'linear-gradient(to right, rgba(120,53,15,0.8), rgba(180,83,9,0.5))',
      registrationBg: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
      registrationText: '#1c1917',
      whyChooseUsBg: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)',
      whyChooseUsText: '#ffffff',
      successStoriesBg: '#fffbeb',
      successStoriesText: '#1c1917',
      ctaBg: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)',
      ctaText: '#ffffff',
    }
  },
};

// Category labels
const categoryLabels = {
  gradient: { label: 'Gradient Themes', icon: Sparkles, color: 'bg-pink-100 text-pink-700' },
  style: { label: 'Solid Themes', icon: Palette, color: 'bg-purple-100 text-purple-700' },
};

// Font options - expanded list
const fontOptions = [
  { value: 'Inter', label: 'Inter', category: 'Sans-serif' },
  { value: 'Poppins', label: 'Poppins', category: 'Sans-serif' },
  { value: 'Roboto', label: 'Roboto', category: 'Sans-serif' },
  { value: 'Open Sans', label: 'Open Sans', category: 'Sans-serif' },
  { value: 'Lato', label: 'Lato', category: 'Sans-serif' },
  { value: 'Nunito', label: 'Nunito', category: 'Sans-serif' },
  { value: 'Montserrat', label: 'Montserrat', category: 'Sans-serif' },
  { value: 'Raleway', label: 'Raleway', category: 'Sans-serif' },
  { value: 'Ubuntu', label: 'Ubuntu', category: 'Sans-serif' },
  { value: 'Oswald', label: 'Oswald', category: 'Sans-serif' },
  { value: 'Quicksand', label: 'Quicksand', category: 'Sans-serif' },
  { value: 'Playfair Display', label: 'Playfair Display', category: 'Serif' },
  { value: 'Merriweather', label: 'Merriweather', category: 'Serif' },
  { value: 'Georgia', label: 'Georgia', category: 'Serif' },
  { value: 'Lora', label: 'Lora', category: 'Serif' },
  { value: 'PT Serif', label: 'PT Serif', category: 'Serif' },
  { value: 'Source Code Pro', label: 'Source Code Pro', category: 'Monospace' },
  { value: 'Fira Code', label: 'Fira Code', category: 'Monospace' },
];

// Font weight options
const fontWeightOptions = [
  { value: '300', label: 'Light', preview: 'Aa' },
  { value: '400', label: 'Regular', preview: 'Aa' },
  { value: '500', label: 'Medium', preview: 'Aa' },
  { value: '600', label: 'Semi-Bold', preview: 'Aa' },
  { value: '700', label: 'Bold', preview: 'Aa' },
  { value: '800', label: 'Extra Bold', preview: 'Aa' },
];

// Font size options
const fontSizeOptions = [
  { value: 'xs', label: 'Extra Small', bodySize: '12px', headingSize: '24px' },
  { value: 'sm', label: 'Small', bodySize: '14px', headingSize: '28px' },
  { value: 'base', label: 'Medium (Default)', bodySize: '16px', headingSize: '32px' },
  { value: 'lg', label: 'Large', bodySize: '18px', headingSize: '36px' },
  { value: 'xl', label: 'Extra Large', bodySize: '20px', headingSize: '42px' },
];

// Border radius options
const borderRadiusOptions = [
  { value: 'small', label: 'Small' },
  { value: 'medium', label: 'Medium' },
  { value: 'large', label: 'Large' },
  { value: 'full', label: 'Pill' }
];

const AdminWebsiteDesigner = () => {
  const [selectedDesign, setSelectedDesign] = useState(null);
  const [currentDesign, setCurrentDesign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewMode, setPreviewMode] = useState('desktop');
  const [activeCategory, setActiveCategory] = useState('all');
  const [showCustomize, setShowCustomize] = useState(false);
  const [customTheme, setCustomTheme] = useState({});
  const [expandedSections, setExpandedSections] = useState({
    colors: true, header: false, footer: false, sections: false, buttons: false, typography: false
  });
  const { toast } = useToast();
  const { updateTheme: applyThemeGlobally } = useTheme();

  useEffect(() => {
    loadCurrentDesign();
  }, []);

  const loadCurrentDesign = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/theme-settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        const designId = data.preset || 'modernMinimal';
        setCurrentDesign(designId);
        setSelectedDesign(designId);
        // Load saved custom theme or use preset defaults
        if (data && Object.keys(data).length > 0) {
          setCustomTheme(data);
        } else if (designPresets[designId]) {
          setCustomTheme({ ...designPresets[designId].theme });
        }
      }
    } catch (error) {
      console.error('Failed to load design:', error);
      setSelectedDesign('modernMinimal');
      setCustomTheme({ ...designPresets.modernMinimal.theme });
    } finally {
      setLoading(false);
    }
  };

  const handleSelectDesign = (designId) => {
    setSelectedDesign(designId);
    const design = designPresets[designId];
    if (design) {
      setCustomTheme({ ...design.theme });
    }
  };

  const updateTheme = useCallback((key, value) => {
    setCustomTheme(prev => ({ ...prev, [key]: value }));
  }, []);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      
      const themeToSave = {
        ...customTheme,
        preset: selectedDesign
      };
      
      console.log('Saving theme:', themeToSave);
      
      const response = await fetch(`${API_URL}/api/admin/theme-settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(themeToSave)
      });
      
      if (response.ok) {
        setCurrentDesign(selectedDesign);
        // Apply theme globally immediately
        try {
          if (applyThemeGlobally) {
            applyThemeGlobally(themeToSave);
          }
        } catch (themeError) {
          console.log('Theme update will apply on refresh:', themeError);
        }
        toast({
          title: "Design Applied! ✨",
          description: "Your changes are now live on the website.",
        });
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Save error:', errorData);
        throw new Error('Failed to save');
      }
    } catch (error) {
      console.error('Save error:', error);
      toast({
        title: "Error",
        description: "Failed to apply design. Please try again.",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const originalTheme = { ...designPresets.modernMinimal.theme, preset: 'modernMinimal' };
      
      await fetch(`${API_URL}/api/admin/theme-settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(originalTheme)
      });
      
      setCurrentDesign('modernMinimal');
      setSelectedDesign('modernMinimal');
      setCustomTheme(originalTheme);
      // Apply reset theme globally
      if (applyThemeGlobally) {
        applyThemeGlobally(originalTheme);
      }
      toast({
        title: "Reset Complete",
        description: "Design reset to default.",
      });
    } catch (error) {
      toast({ title: "Error", description: "Failed to reset", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const filteredDesigns = activeCategory === 'all' 
    ? Object.values(designPresets)
    : Object.values(designPresets).filter(d => d.category === activeCategory);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  // Color Picker Component
  const ColorInput = ({ label, value, onChange, description }) => (
    <div className="space-y-1">
      <Label className="text-xs font-medium text-gray-600">{label}</Label>
      <div className="flex gap-2">
        <input
          type="color"
          value={value?.startsWith('#') ? value : '#000000'}
          onChange={(e) => onChange(e.target.value)}
          className="w-8 h-8 rounded cursor-pointer border border-gray-300"
        />
        <Input
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="#000000"
          className="font-mono text-xs h-8 flex-1"
        />
      </div>
      {description && <p className="text-xs text-gray-400">{description}</p>}
    </div>
  );

  // Gradient Input Component - Fixed version
  const GradientInput = ({ label, value, onChange }) => {
    const isGradient = value?.includes?.('gradient') || false;
    
    // Extract colors from value
    const extractColors = (val) => {
      if (!val) return ['#E91E63', '#9333ea'];
      if (val.includes?.('gradient')) {
        const matches = val.match(/#[a-fA-F0-9]{6}/g);
        return matches && matches.length >= 2 ? [matches[0], matches[1]] : ['#E91E63', '#9333ea'];
      }
      return [val, val];
    };
    
    const initialColors = extractColors(value);
    const [useGradient, setUseGradient] = useState(isGradient);
    const [color1, setColor1] = useState(initialColors[0]);
    const [color2, setColor2] = useState(initialColors[1]);
    const [direction, setDirection] = useState('135deg');

    // Update parent when colors change
    const updateValue = (c1, c2, isGrad, dir) => {
      if (isGrad) {
        onChange(`linear-gradient(${dir}, ${c1} 0%, ${c2} 100%)`);
      } else {
        onChange(c1);
      }
    };

    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-xs font-medium text-gray-600">{label}</Label>
          <button
            type="button"
            onClick={() => {
              const newUseGradient = !useGradient;
              setUseGradient(newUseGradient);
              updateValue(color1, color2, newUseGradient, direction);
            }}
            className={`px-2 py-0.5 text-xs rounded-full ${
              useGradient ? 'bg-gradient-to-r from-pink-500 to-purple-500 text-white' : 'bg-gray-200 text-gray-600'
            }`}
          >
            {useGradient ? 'Gradient' : 'Solid'}
          </button>
        </div>
        
        {useGradient ? (
          <div className="space-y-2 p-2 bg-gray-50 rounded">
            <div 
              className="h-6 rounded"
              style={{ background: `linear-gradient(${direction}, ${color1} 0%, ${color2} 100%)` }}
            />
            <div className="grid grid-cols-2 gap-2">
              <div className="flex gap-1">
                <input 
                  type="color" 
                  value={color1} 
                  onChange={(e) => {
                    setColor1(e.target.value);
                    updateValue(e.target.value, color2, true, direction);
                  }}
                  className="w-6 h-6 rounded cursor-pointer"
                />
                <Input 
                  value={color1} 
                  onChange={(e) => {
                    setColor1(e.target.value);
                    updateValue(e.target.value, color2, true, direction);
                  }}
                  className="font-mono text-xs h-6"
                />
              </div>
              <div className="flex gap-1">
                <input 
                  type="color" 
                  value={color2} 
                  onChange={(e) => {
                    setColor2(e.target.value);
                    updateValue(color1, e.target.value, true, direction);
                  }}
                  className="w-6 h-6 rounded cursor-pointer"
                />
                <Input 
                  value={color2} 
                  onChange={(e) => {
                    setColor2(e.target.value);
                    updateValue(color1, e.target.value, true, direction);
                  }}
                  className="font-mono text-xs h-6"
                />
              </div>
            </div>
            <select 
              value={direction} 
              onChange={(e) => {
                setDirection(e.target.value);
                updateValue(color1, color2, true, e.target.value);
              }}
              className="w-full text-xs p-1 rounded border"
            >
              <option value="0deg">↑ Top</option>
              <option value="90deg">→ Right</option>
              <option value="135deg">↘ Diagonal</option>
              <option value="180deg">↓ Bottom</option>
              <option value="270deg">← Left</option>
            </select>
          </div>
        ) : (
          <div className="flex gap-2">
            <input 
              type="color" 
              value={color1?.startsWith('#') ? color1 : '#E91E63'} 
              onChange={(e) => {
                setColor1(e.target.value);
                setColor2(e.target.value);
                updateValue(e.target.value, e.target.value, false, direction);
              }}
              className="w-8 h-8 rounded cursor-pointer border"
            />
            <Input 
              value={color1 || ''} 
              onChange={(e) => {
                setColor1(e.target.value);
                setColor2(e.target.value);
                updateValue(e.target.value, e.target.value, false, direction);
              }}
              className="font-mono text-xs h-8"
              placeholder="#E91E63"
            />
          </div>
        )}
      </div>
    );
  };

  // Collapsible Section Component
  const Section = ({ title, icon: Icon, name, children }) => (
    <div className="border rounded-lg overflow-hidden">
      <button
        onClick={() => toggleSection(name)}
        className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-purple-500" />
          <span className="font-medium text-sm">{title}</span>
        </div>
        {expandedSections[name] ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
      {expandedSections[name] && (
        <div className="p-4 bg-white">
          {children}
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-purple-500" />
            Website Designer
          </h1>
          <p className="text-gray-600">All design settings in one place</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowCustomize(!showCustomize)}>
            <Edit3 className="w-4 h-4 mr-2" />
            {showCustomize ? 'Hide Editor' : 'Customize'}
          </Button>
          <Button variant="outline" onClick={handleReset} disabled={saving} className="text-red-600">
            <RotateCcw className="w-4 h-4 mr-2" />
            Reset
          </Button>
          <Button onClick={handleSave} disabled={saving} className="bg-purple-600 hover:bg-purple-700">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save Changes
          </Button>
        </div>
      </div>

      {/* Current Design Badge */}
      {currentDesign && designPresets[currentDesign] && (
        <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
          <Check className="w-5 h-5 text-green-600" />
          <span className="text-sm">
            <strong>Active Design:</strong> {designPresets[currentDesign].name}
          </span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Design Presets */}
        <div className="lg:col-span-1 space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Quick Themes</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Category Filter */}
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setActiveCategory('all')}
                  className={`px-3 py-1 text-xs rounded-full ${activeCategory === 'all' ? 'bg-purple-500 text-white' : 'bg-gray-100'}`}
                >
                  All
                </button>
                <button
                  onClick={() => setActiveCategory('gradient')}
                  className={`px-3 py-1 text-xs rounded-full ${activeCategory === 'gradient' ? 'bg-pink-500 text-white' : 'bg-gray-100'}`}
                >
                  Gradient
                </button>
                <button
                  onClick={() => setActiveCategory('style')}
                  className={`px-3 py-1 text-xs rounded-full ${activeCategory === 'style' ? 'bg-blue-500 text-white' : 'bg-gray-100'}`}
                >
                  Solid
                </button>
              </div>

              {/* Preset Grid */}
              <div className="grid grid-cols-2 gap-2 max-h-[500px] overflow-y-auto">
                {filteredDesigns.map((design) => (
                  <button
                    key={design.id}
                    onClick={() => handleSelectDesign(design.id)}
                    className={`p-2 rounded-lg border-2 transition-all text-left ${
                      selectedDesign === design.id 
                        ? 'border-purple-500 bg-purple-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div 
                      className="h-12 rounded mb-2 flex items-center justify-center text-xl"
                      style={{ background: design.theme.headerBackground }}
                    >
                      {design.preview}
                    </div>
                    <p className="text-xs font-medium truncate">{design.name}</p>
                    {currentDesign === design.id && (
                      <span className="text-xs text-green-600">✓ Active</span>
                    )}
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Middle Column - Customization Editor */}
        <div className="lg:col-span-1 space-y-3">
          {showCustomize ? (
            <>
              <h3 className="font-semibold text-gray-700 flex items-center gap-2">
                <Settings className="w-4 h-4" /> Customize All Settings
              </h3>

              {/* Brand Colors */}
              <Section title="Brand Colors" icon={Palette} name="colors">
                <div className="grid grid-cols-2 gap-3">
                  <ColorInput label="Primary" value={customTheme.primaryColor} onChange={(v) => updateTheme('primaryColor', v)} />
                  <ColorInput label="Secondary" value={customTheme.secondaryColor} onChange={(v) => updateTheme('secondaryColor', v)} />
                  <ColorInput label="Accent" value={customTheme.accentColor} onChange={(v) => updateTheme('accentColor', v)} />
                  <ColorInput label="Link Color" value={customTheme.linkColor} onChange={(v) => updateTheme('linkColor', v)} />
                  <ColorInput label="Text Color" value={customTheme.textColor} onChange={(v) => updateTheme('textColor', v)} />
                  <ColorInput label="Light Text" value={customTheme.textLight} onChange={(v) => updateTheme('textLight', v)} />
                  <ColorInput label="Page Background" value={customTheme.backgroundColor} onChange={(v) => updateTheme('backgroundColor', v)} />
                  <ColorInput label="Card Background" value={customTheme.cardBackground} onChange={(v) => updateTheme('cardBackground', v)} />
                </div>
              </Section>

              {/* Header Settings */}
              <Section title="Header / Navbar" icon={Navigation} name="header">
                <div className="space-y-3">
                  <GradientInput 
                    label="Header Background" 
                    value={customTheme.headerBackground} 
                    onChange={(v) => updateTheme('headerBackground', v)} 
                  />
                  <ColorInput label="Header Text" value={customTheme.headerText} onChange={(v) => updateTheme('headerText', v)} />
                </div>
              </Section>

              {/* Footer Settings */}
              <Section title="Footer" icon={Columns} name="footer">
                <div className="space-y-3">
                  <GradientInput 
                    label="Footer Background" 
                    value={customTheme.footerBackground} 
                    onChange={(v) => updateTheme('footerBackground', v)} 
                  />
                  <ColorInput label="Footer Text" value={customTheme.footerText} onChange={(v) => updateTheme('footerText', v)} />
                </div>
              </Section>

              {/* Homepage Sections Settings */}
              <Section title="Homepage Sections" icon={Home} name="sections">
                <div className="space-y-4">
                  {/* Registration Section */}
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs font-semibold text-gray-700 mb-2">📝 Registration Section</p>
                    <div className="space-y-2">
                      <GradientInput 
                        label="Background" 
                        value={customTheme.registrationBg} 
                        onChange={(v) => updateTheme('registrationBg', v)} 
                      />
                      <ColorInput label="Text Color" value={customTheme.registrationText} onChange={(v) => updateTheme('registrationText', v)} />
                    </div>
                  </div>

                  {/* Why Choose Us Section */}
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs font-semibold text-gray-700 mb-2">✨ Why Choose Us Section</p>
                    <div className="space-y-2">
                      <GradientInput 
                        label="Background" 
                        value={customTheme.whyChooseUsBg} 
                        onChange={(v) => updateTheme('whyChooseUsBg', v)} 
                      />
                      <ColorInput label="Text Color" value={customTheme.whyChooseUsText} onChange={(v) => updateTheme('whyChooseUsText', v)} />
                    </div>
                  </div>

                  {/* Success Stories Section */}
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs font-semibold text-gray-700 mb-2">💕 Success Stories Section</p>
                    <div className="space-y-2">
                      <ColorInput label="Background" value={customTheme.successStoriesBg} onChange={(v) => updateTheme('successStoriesBg', v)} />
                      <ColorInput label="Text Color" value={customTheme.successStoriesText} onChange={(v) => updateTheme('successStoriesText', v)} />
                    </div>
                  </div>

                  {/* CTA Section */}
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs font-semibold text-gray-700 mb-2">🚀 Call to Action Section</p>
                    <div className="space-y-2">
                      <GradientInput 
                        label="Background" 
                        value={customTheme.ctaBg} 
                        onChange={(v) => updateTheme('ctaBg', v)} 
                      />
                      <ColorInput label="Text Color" value={customTheme.ctaText} onChange={(v) => updateTheme('ctaText', v)} />
                    </div>
                  </div>
                </div>
              </Section>

              {/* Button Settings */}
              <Section title="Buttons" icon={Square} name="buttons">
                <div className="space-y-4">
                  <div>
                    <p className="text-xs font-medium text-gray-500 mb-2">Primary Button</p>
                    <div className="grid grid-cols-2 gap-2">
                      <GradientInput label="Background" value={customTheme.buttonPrimary} onChange={(v) => updateTheme('buttonPrimary', v)} />
                      <ColorInput label="Text" value={customTheme.buttonPrimaryText} onChange={(v) => updateTheme('buttonPrimaryText', v)} />
                    </div>
                    <button className="mt-2 px-4 py-2 rounded text-sm" style={{ background: customTheme.buttonPrimary, color: customTheme.buttonPrimaryText }}>
                      Preview
                    </button>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500 mb-2">Secondary Button</p>
                    <div className="grid grid-cols-2 gap-2">
                      <ColorInput label="Background" value={customTheme.buttonSecondary} onChange={(v) => updateTheme('buttonSecondary', v)} />
                      <ColorInput label="Text" value={customTheme.buttonSecondaryText} onChange={(v) => updateTheme('buttonSecondaryText', v)} />
                    </div>
                    <button className="mt-2 px-4 py-2 rounded text-sm border" style={{ background: customTheme.buttonSecondary, color: customTheme.buttonSecondaryText }}>
                      Preview
                    </button>
                  </div>
                </div>
              </Section>

              {/* Typography Settings */}
              <Section title="Typography & Fonts" icon={Type} name="typography">
                <div className="space-y-4">
                  {/* Body Font */}
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <Label className="text-xs font-semibold text-gray-700 mb-2 block">📝 Body Font</Label>
                    <select
                      value={customTheme.fontFamily}
                      onChange={(e) => updateTheme('fontFamily', e.target.value)}
                      className="w-full p-2 text-sm border rounded mb-2"
                      style={{ fontFamily: customTheme.fontFamily }}
                    >
                      <optgroup label="Sans-serif">
                        {fontOptions.filter(f => f.category === 'Sans-serif').map(font => (
                          <option key={font.value} value={font.value} style={{ fontFamily: font.value }}>{font.label}</option>
                        ))}
                      </optgroup>
                      <optgroup label="Serif">
                        {fontOptions.filter(f => f.category === 'Serif').map(font => (
                          <option key={font.value} value={font.value} style={{ fontFamily: font.value }}>{font.label}</option>
                        ))}
                      </optgroup>
                      <optgroup label="Monospace">
                        {fontOptions.filter(f => f.category === 'Monospace').map(font => (
                          <option key={font.value} value={font.value} style={{ fontFamily: font.value }}>{font.label}</option>
                        ))}
                      </optgroup>
                    </select>
                    <p className="text-xs" style={{ fontFamily: customTheme.fontFamily }}>
                      Preview: The quick brown fox jumps over the lazy dog.
                    </p>
                  </div>

                  {/* Heading Font */}
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <Label className="text-xs font-semibold text-gray-700 mb-2 block">🔤 Heading Font</Label>
                    <select
                      value={customTheme.headingFont}
                      onChange={(e) => updateTheme('headingFont', e.target.value)}
                      className="w-full p-2 text-sm border rounded mb-2"
                      style={{ fontFamily: customTheme.headingFont }}
                    >
                      <optgroup label="Sans-serif">
                        {fontOptions.filter(f => f.category === 'Sans-serif').map(font => (
                          <option key={font.value} value={font.value} style={{ fontFamily: font.value }}>{font.label}</option>
                        ))}
                      </optgroup>
                      <optgroup label="Serif">
                        {fontOptions.filter(f => f.category === 'Serif').map(font => (
                          <option key={font.value} value={font.value} style={{ fontFamily: font.value }}>{font.label}</option>
                        ))}
                      </optgroup>
                    </select>
                    <h3 className="text-lg font-bold" style={{ fontFamily: customTheme.headingFont }}>
                      Heading Preview
                    </h3>
                  </div>

                  {/* Font Weight */}
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <Label className="text-xs font-semibold text-gray-700 mb-2 block">💪 Font Weight</Label>
                    <div className="grid grid-cols-3 gap-2">
                      {fontWeightOptions.map(weight => (
                        <button
                          key={weight.value}
                          onClick={() => updateTheme('fontWeight', weight.value)}
                          className={`p-2 text-center rounded border transition-all ${
                            customTheme.fontWeight === weight.value 
                              ? 'bg-purple-100 border-purple-500 text-purple-700' 
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <span 
                            className="block text-lg" 
                            style={{ fontWeight: weight.value, fontFamily: customTheme.fontFamily }}
                          >
                            {weight.preview}
                          </span>
                          <span className="text-xs text-gray-500">{weight.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Font Size */}
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <Label className="text-xs font-semibold text-gray-700 mb-2 block">📏 Font Size</Label>
                    <div className="space-y-2">
                      {fontSizeOptions.map(size => (
                        <button
                          key={size.value}
                          onClick={() => updateTheme('fontSize', size.value)}
                          className={`w-full p-2 text-left rounded border transition-all flex items-center justify-between ${
                            customTheme.fontSize === size.value 
                              ? 'bg-purple-100 border-purple-500' 
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <span className="flex items-center gap-2">
                            <span 
                              style={{ fontSize: size.bodySize, fontFamily: customTheme.fontFamily }}
                            >
                              Aa
                            </span>
                            <span className="text-xs text-gray-600">{size.label}</span>
                          </span>
                          <span className="text-xs text-gray-400">
                            Body: {size.bodySize}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Border Radius */}
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <Label className="text-xs font-semibold text-gray-700 mb-2 block">⬜ Border Radius</Label>
                    <div className="flex gap-2">
                      {borderRadiusOptions.map(opt => (
                        <button
                          key={opt.value}
                          onClick={() => updateTheme('borderRadius', opt.value)}
                          className={`flex-1 px-3 py-2 text-xs rounded border transition-all ${
                            customTheme.borderRadius === opt.value 
                              ? 'bg-purple-100 border-purple-500 text-purple-700' 
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </Section>
            </>
          ) : (
            <Card className="h-full flex items-center justify-center">
              <CardContent className="text-center py-12">
                <Edit3 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">Click "Customize" to edit colors, fonts, and more</p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right Column - Live Preview */}
        <div className="lg:col-span-1">
          <Card>
            <CardHeader className="pb-2">
              <div className="flex justify-between items-center">
                <CardTitle className="text-base flex items-center gap-2">
                  <Eye className="w-4 h-4" /> Live Preview
                </CardTitle>
                <div className="flex gap-1">
                  <Button
                    variant={previewMode === 'desktop' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setPreviewMode('desktop')}
                    className="h-7 w-7 p-0"
                  >
                    <Monitor className="w-3 h-3" />
                  </Button>
                  <Button
                    variant={previewMode === 'mobile' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setPreviewMode('mobile')}
                    className="h-7 w-7 p-0"
                  >
                    <Smartphone className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div 
                className={`border-2 border-gray-200 rounded-lg overflow-hidden transition-all mx-auto ${
                  previewMode === 'mobile' ? 'max-w-[280px]' : 'w-full'
                }`}
              >
                {/* Preview Header */}
                <div 
                  className="px-3 py-2 flex justify-between items-center"
                  style={{ background: customTheme.headerBackground }}
                >
                  <span className="font-bold text-sm" style={{ color: customTheme.headerText, fontFamily: customTheme.headingFont }}>
                    qbool.ca
                  </span>
                  <div className="flex gap-2 text-xs" style={{ color: customTheme.headerText }}>
                    <span>Home</span>
                    <span>Search</span>
                  </div>
                </div>

                {/* Preview Content */}
                <div className="p-4" style={{ background: customTheme.backgroundColor, fontFamily: customTheme.fontFamily }}>
                  <h2 className="text-lg font-bold mb-2" style={{ color: customTheme.textColor, fontFamily: customTheme.headingFont }}>
                    Find Your Match
                  </h2>
                  <p className="text-sm mb-3" style={{ color: customTheme.textLight }}>
                    Trusted Islamic matrimonial service.
                  </p>

                  {/* Sample Card */}
                  <div 
                    className="p-3 rounded-lg mb-3"
                    style={{ 
                      background: customTheme.cardBackground,
                      borderRadius: customTheme.borderRadius === 'full' ? '16px' : customTheme.borderRadius === 'large' ? '12px' : '6px'
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: customTheme.primaryColor }}>
                        <Heart className="w-4 h-4 text-white" />
                      </div>
                      <div>
                        <p className="text-sm font-medium" style={{ color: customTheme.textColor }}>Sarah, 28</p>
                        <p className="text-xs" style={{ color: customTheme.textLight }}>Toronto</p>
                      </div>
                    </div>
                  </div>

                  {/* Sample Buttons */}
                  <div className="flex gap-2">
                    <button
                      className="px-3 py-1.5 text-xs font-medium"
                      style={{ 
                        background: customTheme.buttonPrimary, 
                        color: customTheme.buttonPrimaryText,
                        borderRadius: customTheme.borderRadius === 'full' ? '9999px' : '6px'
                      }}
                    >
                      Send Interest
                    </button>
                    <button
                      className="px-3 py-1.5 text-xs font-medium border"
                      style={{ 
                        background: customTheme.buttonSecondary, 
                        color: customTheme.buttonSecondaryText,
                        borderRadius: customTheme.borderRadius === 'full' ? '9999px' : '6px'
                      }}
                    >
                      View Profile
                    </button>
                  </div>
                </div>

                {/* Preview Footer */}
                <div 
                  className="px-3 py-2 text-center text-xs"
                  style={{ background: customTheme.footerBackground, color: customTheme.footerText }}
                >
                  © 2026 qbool.ca
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AdminWebsiteDesigner;
